/**
 * PipelinePulse.tsx — both pipelines as slim segmented FLOW BARS plus the
 * approved-per-day sparkline (v2.1 visual redesign, 2026-08-05).
 *
 * Replaces the two rows of boxed stage cells: zero-count stages disappear,
 * each pipeline reads as one colored bar with a compact legend, and the
 * only chart on the page shows real momentum (tests approved, 14 days).
 */
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import type { DashboardData, StageCell } from '../../types/dashboard';

const STAGE_COLORS: Record<string, string> = {
  intake: '#E65100', draft: '#90A08E', internalReview: '#D4860A', toSend: '#546E96',
  withClient: '#4A6741', accepted: '#2E7D32',
  scheduled: '#90A08E', inProgress: '#D4860A', completed: '#546E96',
  requestReview: '#7B1FA2', reviewed: '#2E7D32',
};

/** Flow bar of the ACTIVE (non-terminal, non-zero) stages. */
const FlowBar = ({ title, subtitle, stages, terminal, linkTo }: {
  title: string;
  subtitle: string;
  stages: StageCell[];
  terminal: StageCell | undefined;
  linkTo: string;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const active = stages.filter((s) => !s.key.endsWith('30') && s.count > 0);
  const total = active.reduce((n, s) => n + s.count, 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        <Typography
          variant="caption" onClick={() => navigate(linkTo)}
          sx={{ ml: 'auto', color: theme.palette.primary.main, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          open →
        </Typography>
      </Box>
      {total === 0 ? (
        <Typography variant="caption" color="text.secondary">Nothing in flight.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', height: 14, borderRadius: '7px', overflow: 'hidden', gap: '2px' }}>
            {active.map((s) => (
              <Tooltip key={s.key} title={`${s.label}: ${s.count}${s.oldestDays ? ` · oldest ${s.oldestDays} d` : ''}${s.note ? ` · ${s.note}` : ''}`} arrow>
                <Box
                  onClick={() => navigate(s.link)}
                  sx={{
                    flex: Math.max(s.count, total * 0.06), cursor: 'pointer',
                    backgroundColor: STAGE_COLORS[s.key] ?? theme.palette.primary.main,
                    transition: 'opacity .15s', '&:hover': { opacity: 0.75 },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', mt: 0.75 }}>
            {active.map((s) => (
              <Typography
                key={s.key} onClick={() => navigate(s.link)} variant="caption"
                sx={{ fontSize: '0.68rem', color: 'text.secondary', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: 'text.primary' } }}
              >
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: STAGE_COLORS[s.key] ?? theme.palette.primary.main, display: 'inline-block' }} />
                {s.label} <b>{s.count}</b>
                {s.oldestDays != null && s.oldestDays >= 5 && (
                  <Box component="span" sx={{ color: theme.palette.error.main, fontWeight: 700 }}>· {s.oldestDays} d</Box>
                )}
              </Typography>
            ))}
            {terminal && terminal.count > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: theme.palette.primary.main, fontWeight: 700, ml: 'auto' }}>
                ✓ {terminal.label.toLowerCase()}: {terminal.count}{terminal.note ? ` · ${terminal.note}` : ''}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

/** Inline SVG sparkline — no chart library. */
const Sparkline = ({ points }: { points: Array<{ date: string; count: number }> }) => {
  const theme = useTheme();
  const w = 260; const hgt = 46;
  const max = Math.max(1, ...points.map((p) => p.count));
  const step = w / Math.max(1, points.length - 1);
  const y = (c: number) => hgt - 4 - (c / max) * (hgt - 10);
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(p.count).toFixed(1)}`).join(' ');
  const area = `${line} L${w},${hgt} L0,${hgt} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${hgt}`} width="100%" height={hgt} preserveAspectRatio="none" role="img" aria-label="Tests approved per day, last 14 days">
      <path d={area} fill={alpha(theme.palette.primary.main, 0.14)} />
      <path d={line} fill="none" stroke={theme.palette.primary.main} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={y(points[points.length - 1]?.count ?? 0)} r={3} fill={theme.palette.primary.main} />
    </svg>
  );
};

export const PipelinePulse = ({ data }: { data: DashboardData }) => {
  const approved14 = data.trend.approvedByDay.reduce((n, p) => n + p.count, 0);
  return (
    <Paper elevation={0} sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'border.color', p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <FlowBar
        title="Quotes"
        subtitle={`${data.quoteRail.open} open · $${Math.round(data.quoteRail.openValue / 1000)}k`}
        stages={data.quoteRail.stages}
        terminal={data.quoteRail.stages.find((s) => s.key === 'converted30')}
        linkTo={ROUTES.QUOTES}
      />
      <FlowBar
        title="Work orders & tests"
        subtitle={`${data.testRail.openWorkOrders} WOs · ${data.testRail.openTests} tests`}
        stages={data.testRail.stages}
        terminal={data.testRail.stages.find((s) => s.key === 'approved30')}
        linkTo={ROUTES.WORK_ORDERS_TAB}
      />
      <Box sx={{ mt: 'auto', pt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Momentum
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {approved14} test{approved14 === 1 ? '' : 's'} approved · last 14 days
          </Typography>
        </Box>
        {approved14 > 0 ? (
          <Sparkline points={data.trend.approvedByDay} />
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1, opacity: 0.7 }}>
            The trend line appears here once tests get approved.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
